// utils/scalePage.ts

/**
 * Dynamically scales the page by adjusting or removing the `--font-size-base` CSS variable.
 * The function performs an early exit for screen widths less than 1500px to minimise unnecessary work.
 *
 * Dependencies:
 * - `debounce` function for efficient resize event handling.
 */

import { debounce } from './debounce';

/**
 * Dynamically scales the page by adjusting or removing the `--font-size-base` CSS variable.
 */
export function scalePage(): void {
	const basePercentage = 62.5; // Base size as percentage (62.5%)
	const referenceWidth = 2500; // Reference width for scaling (1500px)
	const screenWidth = window.innerWidth;

	// Early exit for widths < 1500px to avoid unnecessary style manipulation
	if (screenWidth < referenceWidth) {
		// Only remove the variable if it is currently set (avoids redundant operations)
		if (document.documentElement.style.getPropertyValue('--font-size-base')) {
			document.documentElement.style.removeProperty('--font-size-base');
		}
		return; // Exit early
	}

	// Calculate scaling factor for widths >= 1500px
	const scalingFactor = (screenWidth / referenceWidth) * basePercentage;

	// Set the `--font-size-base` CSS variable
	document.documentElement.style.setProperty('--font-size-base', `${scalingFactor}%`);
}

/**
 * Initialises the dynamic page scaling with debounced resize handling.
 */
export function initialisePageScaling(): void {
	// Scale the page on load
	scalePage();

	// Debounce the resize event
	const debouncedResize = debounce(scalePage, 150);

	// Recalculate on window resize
	window.addEventListener('resize', debouncedResize);
}
